// DeskOfferService — the desk's quote and options (PB3, PMS-PHONE-BOOKING-BRIEF §5).
//
// The website's price for a caller on the phone (P1): the same bounds as the
// booking engine (`stayQueryProblem`, refused before any round trip), the same
// facts (`pms_desk_offer` is `pms_public_offer` plus standing and lodging), the
// same pure functions (`deskSale` = `sellAs` + `quoteStay`), and the same
// renderer for the terms. What differs is who may ask: L2 here
// (`pms.reservation.write`), L4 in the RPC (the capability and the établissement
// in reach).

import { CAPABILITIES, type ActorSource } from '@lemontaigu/platform';
import {
  deskOptions,
  deskSale,
  err,
  ok,
  renderTermsText,
  type AgeBand,
  type Clock,
  type DeskOfferRepository,
  type DeskOfferService,
  type DeskQuote,
  type DeskQuoteQuery,
  type DeskSale,
  type DeskStayQuery,
  type Result,
  type StayParty,
} from '@lemontaigu/pms-core';
import { stayQueryProblem } from './booking/bookingEngine.js';
import { gate } from './services.js';

/** The bands a child may be given (P5). An adult is not a child, and an infant is counted apart. */
const CHILD_BANDS: readonly AgeBand[] = ['child_13_17', 'child_9_12', 'child_6_8', 'child_3_5', 'child_unknown'];

/** The bounds, as one French sentence or null. Exported for its tests. */
export function deskQueryProblem(q: DeskStayQuery, today: string): string | null {
  const stay = stayQueryProblem(q, today);
  if (stay !== null) return stay;
  const bands = q.childBands ?? [];
  if (!Array.isArray(bands) || bands.length > q.children) return "Plus d'âges que d'enfants.";
  if (bands.some((b) => !CHILD_BANDS.includes(b))) return "Tranche d'âge d'enfant invalide.";
  return null;
}

/** The party as the quote echoes it: `childBands` only when the desk gave ages. */
const partyOf = (q: DeskStayQuery): StayParty =>
  q.childBands === undefined
    ? { adults: q.adults, children: q.children, infants: q.infants }
    : { adults: q.adults, children: q.children, infants: q.infants, childBands: q.childBands };

export class DeskOfferServiceImpl implements DeskOfferService {
  constructor(
    private readonly offers: DeskOfferRepository,
    private readonly actor: ActorSource,
    private readonly clock: Clock,
  ) {}

  async options(query: DeskStayQuery): Promise<Result<DeskSale[]>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return denied;
    const problem = deskQueryProblem(query, this.clock.todayParis());
    if (problem) return err('validation', problem, { retryable: false });
    const facts = await this.offers.offer(query.propertyCode, query.from, query.to);
    if (!facts.ok) return facts;
    return ok(deskOptions(facts.value, partyOf(query)));
  }

  async quote(query: DeskQuoteQuery): Promise<Result<DeskQuote>> {
    const denied = gate(this.actor, CAPABILITIES.PMS_RESERVATION_WRITE);
    if (denied) return denied;
    const problem = deskQueryProblem(query, this.clock.todayParis());
    if (problem) return err('validation', problem, { retryable: false });
    const facts = await this.offers.offer(query.propertyCode, query.from, query.to);
    if (!facts.ok) return facts;

    const sale = deskSale(facts.value, query.category, query.plan, partyOf(query));
    if (!sale.sellable) return ok({ sale });
    // The plan's terms for THIS stay, by the one renderer (P11): the dates and
    // amounts the desk reads out are the ones the site shows and the stay copies.
    const rules = facts.value.plans.find((p) => p.code === query.plan)?.terms ?? null;
    const terms =
      rules === null
        ? null
        : renderTermsText(rules, { arrival: query.from, totalCents: sale.quote.totalCents, bookedOn: this.clock.todayParis() });
    return ok({ sale, terms });
  }
}
